import { useAgentCapabilities } from "@/api/harnessCapabilities";
import {
  AGENT_IDS,
  AgentCapability,
  agentOf,
  findAgent,
  isCohesiveHarness,
} from "@/constants/agents";
import { defaultModelFor } from "@/constants/harnesses";
import {
  CreateFlowSessionRequest,
  HARNESS_IDS,
  HarnessId,
} from "@cohesive/api-client";
import { useLocalStorageState } from "ahooks";
import { useCallback, useMemo } from "react";

export interface HarnessDraft {
  /** The agent, as the catalog row it runs as (Cohesive on either loop). */
  harness: HarnessId;
  /** Set only on a subscription agent; Cohesive's tier picks its model server-side. */
  model?: string;
  /** The thinking picked for `model`; absent is the model's default. Dropped with the model. */
  effort?: string;
}

/**
 * What a surface's draft is stored as: the pick, stamped with the default it was made under
 * (`harnessDefaultStamp`). A draft whose stamp no longer matches the default was made for an
 * older default and stops applying; one without a stamp predates the preference, when the only
 * default was Cohesive.
 */
interface StoredHarnessDraft extends HarnessDraft {
  seed?: string;
}

const DEFAULT_DRAFT: HarnessDraft = { harness: HARNESS_IDS.COHESIVE };

/** The draft that starts a chat on `agent`, at its default model. */
export function agentDraft(agent: AgentCapability): HarnessDraft {
  return agent.id === AGENT_IDS.COHESIVE
    ? { harness: agent.harness }
    : { harness: agent.harness, model: defaultModelFor(agent) };
}

/**
 * Coerce a stored draft onto an agent the user can start a chat on today. Cohesive — on whichever
 * loop the catalog offers it — is the fallback for anything else: a harness the catalog no longer
 * lists, or one that is no agent's (opencode). A subscription agent the user picked but has not
 * connected stays picked: that is the state that asks them to connect. A Cohesive pick follows the
 * catalog's row, so a stored one survives harness-runtime flips.
 *
 * Unknown agents — still loading, or the request failed — fail closed to the legacy harness, the
 * one the server accepts without the harness-runtime flag. Read-only: the stored choice is
 * untouched, so it comes back intact the moment the catalog does.
 */
export function sanitizeHarnessDraft(
  draft: HarnessDraft,
  agents: AgentCapability[] | undefined,
): HarnessDraft {
  if (!agents?.length) return DEFAULT_DRAFT;

  const cohesive = findAgent(agents, AGENT_IDS.COHESIVE);
  const cohesiveDraft = cohesive ? agentDraft(cohesive) : DEFAULT_DRAFT;

  const agent = isCohesiveHarness(draft.harness)
    ? undefined
    : findAgent(agents, agentOf(draft.harness));
  if (!agent || agent.harness !== draft.harness) return cohesiveDraft;

  const kept = agent.models.find((m) => m.id === draft.model);
  const model = kept ? draft.model : defaultModelFor(agent);
  const effort = kept?.efforts?.some((e) => e.id === draft.effort)
    ? draft.effort
    : undefined;
  return { harness: agent.harness, model, ...(effort ? { effort } : {}) };
}

export type HarnessCreateFields = Pick<
  CreateFlowSessionRequest,
  "harness" | "model"
>;

/**
 * The create-payload fields a draft stamps on the session it starts. A subscription the user
 * picked but has not connected cannot be stamped (the server refuses it), so the session starts
 * on Cohesive and the composer asks to connect from there. Cohesive names no model.
 */
export function harnessCreateFields(
  draft: HarnessDraft,
  agents: AgentCapability[] | undefined,
): HarnessCreateFields {
  const cohesive = findAgent(agents, AGENT_IDS.COHESIVE);
  const cohesiveFields = {
    harness: cohesive?.harness ?? DEFAULT_DRAFT.harness,
  };
  if (isCohesiveHarness(draft.harness)) return cohesiveFields;
  const agent = findAgent(agents, agentOf(draft.harness));
  if (!agent?.connected) return cohesiveFields;
  return { harness: draft.harness, model: draft.model };
}

/**
 * The agent and model a new chat starts on when nothing picks one: the catalog's resolved default
 * — the user's preference where it still applies, else Cohesive — with the model the server would
 * fill. Undefined until the catalog lands.
 */
export function harnessDefaultOf(
  agents: AgentCapability[] | undefined,
): HarnessDraft | undefined {
  if (!agents) return undefined;
  const agent =
    agents.find((a) => a.default) ?? findAgent(agents, AGENT_IDS.COHESIVE);
  if (!agent) return DEFAULT_DRAFT;
  return agent.id === AGENT_IDS.COHESIVE
    ? { harness: agent.harness }
    : { harness: agent.harness, model: defaultModelFor(agent, true) };
}

export function useHarnessDefault(): HarnessDraft | undefined {
  const { data: agents } = useAgentCapabilities();
  return useMemo(() => harnessDefaultOf(agents), [agents]);
}

/**
 * The default as a draft is stamped with it: the harness, then the model. Cohesive stamps as
 * `cohesive` whichever loop serves it, so the server moving Cohesive onto
 * cohesive-claude-code is not a change of default.
 */
export function harnessDefaultStamp(draft: HarnessDraft): string {
  if (isCohesiveHarness(draft.harness)) return HARNESS_IDS.COHESIVE;
  return draft.model ? `${draft.harness}/${draft.model}` : draft.harness;
}

/**
 * The draft a surface reads: its stored pick while that pick was made under the current
 * default, else the default itself. A pick stored before the stamp counts as made under
 * Cohesive's. Exported for the seeding rule's tests; `useHarnessDraft` is what surfaces call.
 */
export function seedHarnessDraft(
  stored: StoredHarnessDraft | undefined,
  harnessDefault: HarnessDraft | undefined,
  agents: AgentCapability[] | undefined,
): HarnessDraft {
  if (!harnessDefault) return DEFAULT_DRAFT;
  const draft =
    stored &&
    (stored.seed ?? HARNESS_IDS.COHESIVE) ===
      harnessDefaultStamp(harnessDefault)
      ? stored
      : harnessDefault;
  return sanitizeHarnessDraft(draft, agents);
}

/**
 * Persists a session-less composer's agent + model choice under `storageKey`, seeds it from the
 * user's default where none is stored, and coerces it back to Cohesive when what it names is no
 * longer selectable. Scope-agnostic, like `useReasoningEffort` next door, so each composer surface
 * keeps its own choice.
 *
 * A pick is this surface's alone, but only for as long as the default it was made under stands:
 * change the default and every surface re-seeds from it, the stored picks ignored rather than
 * swept. Draft state only: inside a chat the agent is stamped on the flow session and the model is
 * read from it, so neither is a local preference there.
 */
export function useHarnessDraft(storageKey: string) {
  const { data: agents, isPending: catalogPending } = useAgentCapabilities();
  const harnessDefault = useHarnessDefault();
  // The choice is read by hooks other than the one the chip writes through — the canvas draft
  // has six live instances, and the new-chat buttons that spend it are never the composer that
  // set it. Without this each instance keeps the snapshot it mounted with and mints on an agent
  // the user has since changed, which the create then stamps for good.
  const [stored, setStored] = useLocalStorageState<StoredHarnessDraft>(
    storageKey,
    { listenStorageChange: true },
  );

  // Memoized because the sanitized draft is what the session-create callbacks depend on: a
  // fresh object each render would give every one of them a new identity, and they are props
  // on surfaces that rely on their stability.
  const draft = useMemo(
    () => seedHarnessDraft(stored, harnessDefault, agents),
    [stored, harnessDefault, agents],
  );
  // The chip and the mint read this one draft, so they cannot disagree. `ready` is the catalog
  // this page load fetched (it is never persisted) having answered — or failed, which falls to
  // Cohesive: until then nothing may mint a chat on the draft.
  const createFields = useMemo(
    () => harnessCreateFields(draft, agents),
    [draft, agents],
  );
  const ready = !catalogPending;

  const setDraft = useCallback(
    (next: HarnessDraft) =>
      setStored({
        ...next,
        seed: harnessDefaultStamp(harnessDefault ?? DEFAULT_DRAFT),
      }),
    [setStored, harnessDefault],
  );

  return [draft, setDraft, createFields, ready] as const;
}

/**
 * The canvas's standing agent choice, keyed the way `useCanvasReasoningEffort` keys its tier.
 * Every user-started chat on a canvas reads it — the composer that offers the chip and the
 * new-chat buttons that mint a session before the user has typed anything — so a canvas answers
 * consistently however the chat was opened.
 */
export function useCanvasHarnessDraft(canvasId: string | undefined) {
  return useHarnessDraft(`canvas_harness_${canvasId ?? "__none__"}`);
}
